import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import Users_no_row, { UserCard } from './users_row';
import Pagination from '@/components/ui/pagination';
import { userService } from "../../../_services";
import Edit_user_pop from './EditUserDialog.jsx';
import Refferral_pop from './ReferralManagementDialog.jsx';
import ChangePass from './ChangepassPop.jsx';
import AddUser from './AddUserDialog.jsx';
import DeactivateRefferalLinks from '../Refferal_Link/DeactivateReferralLinksDialog.jsx';
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableHead, TableHeader, TableRow, TableCell } from "@/components/ui/table";
import { Search, UserPlus, Loader2, X } from "lucide-react";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";

/**
 * Users Management Page
 * Modernized with shadcn components and debounced search
 */
function User_Page() {
    const location = useLocation();
    const searchInputRef = useRef(null);
    const searchTimerRef = useRef(null);

    // State management
    const [allUserData, setAllUserData] = useState([]);
    const [userData, setUserData] = useState([]);
    const [pageOfItems, setPageOfItems] = useState([]);
    const [searchInput, setSearchInput] = useState("");
    const [activeTab, setActiveTab] = useState('all');
    const [activeSetting, setActiveSetting] = useState(null);
    const [loading, setLoading] = useState(false);
    const [isSearching, setIsSearching] = useState(false);
    
    // Dialog states
    const [showEditPopup, setShowEditPopup] = useState(false);
    const [showReferralPopup, setShowReferralPopup] = useState(false);
    const [showChangePassword, setShowChangePassword] = useState(false);
    const [showDeactivateLinks, setShowDeactivateLinks] = useState(false);
    const [showAddUser, setShowAddUser] = useState(false);
    const [showInactivePopup, setShowInactivePopup] = useState(false);
    
    // Selected user states
    const [selectedUserFullname, setSelectedUserFullname] = useState('');
    const [selectedRepId, setSelectedRepId] = useState('');
    const [selectedUsername, setSelectedUsername] = useState('');
    const [selectedUserId, setSelectedUserId] = useState('');

    // Fetch users on mount
    useEffect(() => {
        fetchUsers();
    }, []);

    // Handle location state for pre-filled search after data loads
    useEffect(() => {
        if (allUserData.length > 0 && location.state?.property_id) {
            setSearchInput(location.state.property_id);
        }
    }, [allUserData]);

    const fetchUsers = async () => {
        setLoading(true);
        try {
            const Usermeta_Data = window.atob(localStorage.getItem('ajs_user_id'));
            const [userId, userName] = Usermeta_Data.split(':');
            
            const apidata = await userService.GetUserList(userId, userName, '-1');
            
            if (apidata && apidata.length > 0) {
                setAllUserData(apidata);
                setUserData(apidata);
            } else {
                setAllUserData([]);
                setUserData([]);
            }
        } catch (error) {
            console.error('Error fetching users:', error);
            setAllUserData([]);
            setUserData([]);
        } finally {
            setLoading(false);
        }
    };

    // Compute filtered list from allUserData whenever search text or tab changes.
    const filteredData = useMemo(() => {
        let filtered = [...allUserData];

        // Tab filter
        if (activeTab !== 'all') {
            filtered = filtered.filter(user =>
                (user.userType || '').toLowerCase().includes(activeTab.toLowerCase())
            );
        }

        // Text search
        if (searchInput.trim()) {
            const search = searchInput.toLowerCase();
            filtered = filtered.filter(user => {
                const uname = user.userName?.toLowerCase() || '';
                const fullName = `${user.firstName || ''} ${user.lastName || ''}`.toLowerCase();
                const email = user.email?.toLowerCase() || '';
                const groupNumber = user.groupNumber?.toLowerCase() || '';
                return (
                    uname.includes(search) ||
                    fullName.includes(search) ||
                    email.includes(search) ||
                    groupNumber.includes(search)
                );
            });
        }

        return filtered.length > 0 ? filtered : [{}];
    }, [allUserData, activeTab, searchInput]);

    // Keep userData in sync with filteredData so Pagination receives updated items.
    useEffect(() => {
        setUserData(filteredData);
    }, [filteredData]);

    // Debounced search input
    const handleSearchChange = (value) => {
        setIsSearching(true);
        setSearchInput(value);
        if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
        searchTimerRef.current = setTimeout(() => setIsSearching(false), 300);
    };

    const clearSearch = () => {
        setSearchInput('');
        if (searchInputRef.current) searchInputRef.current.focus();
    };

    const handleTabChange = (tab) => {
        setActiveTab(tab);
    };

    const handleEditUser = ({ userFullname, repid }) => {
        setSelectedUserFullname(userFullname);
        setSelectedRepId(repid);
        setShowEditPopup(true);
    };

    const closeEditUser = () => {
        setShowEditPopup(false);
        refreshUserList();
    };

    const handleReferrals = ({ userFullname, numbering }) => {
        setSelectedUserFullname(userFullname);
        setSelectedRepId(numbering);
        setShowReferralPopup(true);
    };

    const handleChangePassword = ({ repid }) => {
        setSelectedRepId(repid);
        setShowChangePassword(true);
    };

    const closeChangePassword = () => {
        setShowChangePassword(false);
        refreshUserList();
    };

    const handleDeactivateLinks = ({ uname, uid }) => {
        setSelectedUsername(uname);
        setSelectedUserId(uid);
        setShowDeactivateLinks(true);
    };

    const closeDeactivateLinks = () => setShowDeactivateLinks(false);

    const handleActivateDeactivate = async ({ status, repid }) => {
        const Usermeta_Data = window.atob(localStorage.getItem('ajs_user_id'));
        const [userId, userName] = Usermeta_Data.split(':');

        try {
            await userService.UpdateUserStatus(userId, userName, repid, status);
            refreshUserList();
        } catch (error) {
            console.error('Error updating user status:', error);
        }
    };

    const handleResendActivation = async ({ userId, userName }) => {
        try {
            await userService.ResendAccountActivationEmail(userId, userName);
        } catch (error) {
            console.error('Error resending activation:', error);
        }
    };

    const handleTierCommission = async ({ userId, userName, email, currentValue }) => {
        const tiercomm = currentValue === true || currentValue === "true" ? false : true;
        try {
            await userService.UpdateTierCommissionSetting(userId, userName, email, tiercomm);
            setTimeout(() => refreshUserList(), 2000);
        } catch (error) {
            console.error('Error updating tier commission:', error);
        }
    };

    const handleSecondaryCard = async ({ userId, userName, email, currentValue }) => {
        const seccard = currentValue === true || currentValue === "true" ? false : true;
        try {
            await userService.cardSetting(userId, userName, email, seccard);
            setTimeout(() => refreshUserList(), 2000);
        } catch (error) {
            console.error('Error updating secondary card:', error);
        }
    };

    const refreshUserList = () => {
        fetchUsers();
    };

    const onChangePage = (pageOfItems) => {
        setPageOfItems(pageOfItems);
    };

    // Auth check
    if (userService.Check_Null_Empty(localStorage.getItem("ajs_user_id"))) {
        return <Navigate replace to="/signup" />;
    }

    if (window.atob(localStorage.getItem('UserRole')) !== "Admin") {
        return <Navigate replace to="/page-not-found" />;
    }

    localStorage.setItem('pagename', 'userspage');

    const dataToDisplay = pageOfItems;

    // Shared row props
    const rowProps = {
        toggle_add_edit_popup: handleEditUser,
        toggle_refferal_pop: handleReferrals,
        ClosePopUp: handleChangePassword,
        activate_deactivate: handleActivateDeactivate,
        deactivate_refferals_links: handleDeactivateLinks,
        setStateOfParent: (apidata, tabname) => {
            setUserData(apidata);
            setAllUserData(apidata);
            handleTabChange(tabname || 'all');
        },
        show_settings: activeSetting,
        setActiveSetting: setActiveSetting,
        resendActivationApi: handleResendActivation,
        TierCommission: handleTierCommission,
        SecondaryCard: handleSecondaryCard,
        tabName: activeTab,
    };

    return (
        <div className="w-full max-w-full overflow-hidden space-y-6">
            {/* Heading */}
            <h1 className="text-3xl font-bold tracking-tight text-foreground">Users</h1>

            {/* Search + Tabs + Add Button */}
            <div className="flex flex-wrap items-center justify-between gap-y-3 gap-x-4">
                {/* Search */}
                <div className="relative w-full max-w-md flex-1 min-w-[200px]">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-primary/50" />
                    <Input
                        ref={searchInputRef}
                        type="text"
                        placeholder="Search users..."
                        value={searchInput}
                        onChange={(e) => handleSearchChange(e.target.value)}
                        disabled={loading || !allUserData.length}
                        className="pl-10 pr-9"
                    />
                    {isSearching && (
                        <Loader2 className="absolute right-10 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-muted-foreground/70" />
                    )}
                    {searchInput && !isSearching && (
                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={clearSearch}
                            className="absolute right-2 top-1/2 -translate-y-1/2 h-6 w-6"
                            aria-label="Clear search"
                            type="button"
                        >
                            <X className="h-4 w-4" />
                        </Button>
                    )}
                </div>

                {/* Tabs + Add Users button */}
                <div className="flex flex-wrap items-center gap-3">
                    <Tabs value={activeTab} onValueChange={handleTabChange}>
                        <TabsList className="bg-background">
                            <TabsTrigger value="all" disabled={loading || !allUserData.length}>
                                All
                            </TabsTrigger>
                            <TabsTrigger value="distributor" disabled={loading || !allUserData.length}>
                                Distributor
                            </TabsTrigger>
                            <TabsTrigger value="consumer" disabled={loading || !allUserData.length}>
                                Consumer
                            </TabsTrigger>
                        </TabsList>
                    </Tabs>

                    <Button
                        onClick={() => setShowAddUser(true)}
                        disabled={loading}
                        variant="secondary"
                    >
                        <UserPlus className="mr-2 h-4 w-4" />
                        Add Users
                    </Button>
                </div>
            </div>

            {/* -- Desktop table (md and above) -- */}
            <Card className="hidden md:block">
                <CardContent className="p-0">
                    {loading ? (
                        <div className="flex items-center justify-center py-12">
                            <Loader2 className="h-8 w-8 animate-spin text-accent" />
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead className="text-primary">User Name</TableHead>
                                        <TableHead className="text-primary">Full Name</TableHead>
                                        <TableHead className="text-primary">Email</TableHead>
                                        <TableHead className="text-primary">Primary Group Number</TableHead>
                                        <TableHead className="text-primary">Status</TableHead>
                                        <TableHead className="text-primary">Approve Payment</TableHead>
                                        <TableHead className="text-primary text-right">Actions</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {dataToDisplay.length > 0 ? (
                                        dataToDisplay.map((userRow, index) => (
                                            <Users_no_row
                                                key={userRow.userId || index}
                                                index={index}
                                                {...userRow}
                                                {...rowProps}
                                            />
                                        ))
                                    ) : (
                                        <TableRow>
                                            <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                                                No records found
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* -- Mobile / tablet card list (below md) -- */}
            <div className="md:hidden space-y-3">
                {loading ? (
                    <Card>
                        <CardContent className="flex items-center justify-center p-4 py-12 sm:p-6 sm:py-12">
                            <Loader2 className="h-8 w-8 animate-spin text-accent" />
                        </CardContent>
                    </Card>
                ) : dataToDisplay.length > 0 ? (
                    dataToDisplay.map((userRow, index) => (
                        <UserCard
                            key={userRow.userId || index}
                            index={index}
                            {...userRow}
                            {...rowProps}
                        />
                    ))
                ) : (
                    <Card>
                        <CardContent className="text-center p-4 py-8 sm:p-6 sm:py-8 text-muted-foreground">
                            No records found
                        </CardContent>
                    </Card>
                )}
            </div>

            {/* Pagination */}
            {!loading && (
                <div className="px-1">
                    <Pagination
                        items={userData}
                        onChangePage={onChangePage}
                        showInfo={true}
                    />
                </div>
            )}

            {/* Modals */}
            {showInactivePopup && (
                <ConfirmDialog
                    open={showInactivePopup}
                    onOpenChange={setShowInactivePopup}
                    title="Inactive User"
                    description="This user needs to verify their email address before their account can be activated."
                    onConfirm={() => setShowInactivePopup(false)}
                    confirmText="Ok"
                    showIcon={false}
                />
            )}

            <Edit_user_pop
                open={showEditPopup}
                onOpenChange={(open) => { if (!open) setShowEditPopup(false); }}
                repid={selectedRepId}
                onSuccess={closeEditUser}
            />

            <Refferral_pop
                open={showReferralPopup}
                onOpenChange={setShowReferralPopup}
                title={`${selectedUserFullname} Referrals`}
                repid={selectedRepId}
            />

            <ChangePass
                open={showChangePassword}
                onOpenChange={(open) => { if (!open) setShowChangePassword(false); }}
                onSuccess={closeChangePassword}
                repid={selectedRepId}
            />

            {showDeactivateLinks && (
                <DeactivateRefferalLinks
                    uname={selectedUsername}
                    uid={selectedUserId}
                    deactivate_refferals_links={closeDeactivateLinks}
                />
            )}

            {showAddUser && (
                <AddUser
                    open={showAddUser}
                    onOpenChange={setShowAddUser}
                    onSuccess={() => {
                        setShowAddUser(false);
                        refreshUserList();
                    }}
                />
            )}
        </div>
    );
}

export default User_Page;

